import { useState } from "react";
import { BarChart2, ChevronDown, ChevronUp } from "lucide-react";
import { mockForecastSummary } from "@/lib/mock-data/forecast-summary";
import { buildForecastPerformance } from "../utils/forecast-performance";
import { CostForecastCard } from "./cost-forecast-card";
import { RevenueForecastCard } from "./revenue-forecast-card";
import { ForecastMarginCard } from "./forecast-margin-card";

interface ForecastPerformanceSectionProps {
  onViewForecast: () => void;
}

/**
 * Forecast Performance section of the Financials tab. Reads the same forecast
 * snapshot as the Forecast tab; in production this comes from a shared context
 * or server snapshot.
 */
export function ForecastPerformanceSection({ onViewForecast }: ForecastPerformanceSectionProps) {
  const [expanded, setExpanded] = useState(true);
  const performance = buildForecastPerformance(mockForecastSummary);
  const { lastUpdatedFormatted } = performance;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <BarChart2 className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold text-foreground">Forecast Performance</h3>
        <div className="flex-1 h-px bg-border ml-1" />
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {lastUpdatedFormatted ? `Snapshot: ${lastUpdatedFormatted}` : 'No snapshot yet'}
        </span>
        <button
          type="button"
          onClick={() => setExpanded(e => !e)}
          className="p-0.5 rounded hover:bg-muted transition-colors ml-1"
          aria-label={expanded ? 'Collapse Forecast Performance' : 'Expand Forecast Performance'}
        >
          {expanded
            ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
            : <ChevronDown className="h-4 w-4 text-muted-foreground" />
          }
        </button>
      </div>

      {expanded && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <CostForecastCard performance={performance} onViewForecast={onViewForecast} />
          <RevenueForecastCard performance={performance} onViewForecast={onViewForecast} />
          <ForecastMarginCard performance={performance} />
        </div>
      )}
    </div>
  );
}
